import { useMemo, useState } from "react";
import { Search, Briefcase, FileDown, Pill, AlertOctagon } from "lucide-react";
import { DISEASES, CATEGORIES } from "../lib/diseases";
import { MEDICATIONS } from "../lib/medications";
import { buildFirstAidKitPdf } from "../lib/diseasePdf";

// Heuristic drug class buckets — alphabetically ordered, fall back to "Other"
const CLASS_RULES = [
  { id: "antibiotic",      label: "Antibiotics & Antimicrobials", match: /cillin|cycline|cef|cipro|levo|fluoro|rifa|isoniazid|pyrazinamide|ethambutol|mupirocin|erythromycin|moxiflox|sulfa|trimeth|metronida|azithro|gentamicin|streptomycin|linezolid|bedaquiline|clofazimine|penicillin|bcg/i },
  { id: "antiviral",       label: "Antivirals (PEP)",             match: /tenofovir|emtricit|raltegravir|dolutegravir|zidovudine|truvada/i },
  { id: "nsaid",           label: "NSAIDs & Analgesics",          match: /ibuprofen|naproxen|diclofenac|celecoxib|acetamin|tylenol|aspirin|ketorolac|morphine|lidocaine patch/i },
  { id: "steroid",         label: "Corticosteroids",              match: /predniso|methylpred|dexameth|triamcin|betameth|clobetasol|fluocin|hydrocort|mometasone|budesonide|fluticasone/i },
  { id: "bronchodilator",  label: "Bronchodilators & Inhaled",    match: /albuterol|salbutamol|levalbuterol|ipratropium|tiotropium|salmeterol|montelukast|zafirlukast|advair|seretide/i },
  { id: "antihistamine",   label: "Antihistamines",               match: /cetirizine|loratadine|diphenhydramine|hydroxyzine|doxepin|fexofen/i },
  { id: "chelator",        label: "Chelators & Antidotes",        match: /dmsa|dimercaprol|bal|edta|d-penicillamine|prussian|dtpa|deferasirox|hydroxocobal|nitrite|cyanokit|atropine|pralidoxime|2-pam|charcoal|iodide|calcium gluc|diphoterine/i },
  { id: "psychiatric",     label: "Psychiatric & Neuro",          match: /sertraline|fluoxetine|paroxetine|venlafax|citalopram|prazosin|quetiap|mirtazap|topiramate|clonidine|gabapent|pregabal|levodopa|trihexyphenidyl|midazolam|diazepam|lorazep/i },
  { id: "cardio",          label: "Cardiovascular & Antiplatelet",match: /amlodip|nifedip|sildenafil|tadalafil|aspirin|warfarin|enoxap|apixab|rivarox|dabigat|heparin|alteplase|pentoxifyl|iloprost/i },
  { id: "chemo",           label: "Oncology / Cytotoxic",         match: /cisplatin|carboplatin|pemetrexed|paclitaxel|docetaxel|doxorub|gemcitabine|vinorelbine|cytarabine|daunorub|hydroxyurea|lenalidomide|trabectedin|ifosfamide|cetuximab|pembrolizumab|nivolumab|ipilimumab|atezolizumab|bevacizumab|imatinib|midostaurin|gilteritinib|venetoclax|enfortumab|osimertinib|alectinib|pazopanib|durvalumab|mesna|all-trans|arsenic trioxide/i },
  { id: "biologic",        label: "Biologics & Growth Factors",   match: /filgrastim|pegfilgrastim|sargramostim|romiplostim|eltrombopag|erythropoietin|epogen|rituximab|raxibacumab|obiltoxaximab|anthrasil|biothrax|methotrexate|azathioprine|mycophenolate/i },
  { id: "supportive",      label: "Vitamins & Supportive",        match: /vitamin|methylcobal|b12|magnesium|coenzyme|ginkgo|n-acetylcyst|nac|colchicine|dantrolene|tetanus|aloe|silver sulfa|hyaluronic|prp|fluoride|stannous|potassium nitrate|sodium bicarb|cholestyramine|cyclopentolate|tropicamide|homatropine|proparacaine|tetracaine|fluoride varnish|casein/i },
  { id: "respiratory-other", label: "Antifibrotic / Mucolytic",   match: /pirfenidone|esbriet|nintedanib/i },
  { id: "topical",         label: "Topical & Dermatologic",       match: /pimecrolimus|tacrolimus|isotretinoin|adapalene|benzoyl|tretinoin|hydroquinone|kojic|azelaic|petroleum|petrolatum/i },
  { id: "oxygen",          label: "Oxygen / Procedures / Devices",match: /oxygen|hyperbaric|hbot|surgical|graduated compression|vena cava|portable hyperbaric|ice|cold iv|allopurinol|water irrigation|dental|composite|saline nasal|surgical septal|cataract|tpa$|tissue plasm|phacoemulsification|radioactive iodine|intensity-modulated|allogeneic|inferior vena/i },
];

const classify = (name) => {
  for (const r of CLASS_RULES) if (r.match.test(name)) return r;
  return { id: "other", label: "Other" };
};

// Build the deduped kit:
// Keys aggregated by lowercased generic name (strip trailing parenthetical), preserving canonical display.
const buildKit = () => {
  const map = new Map();
  DISEASES.forEach((d) => {
    const meds = MEDICATIONS[d.id] || [];
    meds.forEach((m) => {
      const key = m.name.trim().toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          name: m.name,
          brand: m.brand && m.brand !== "—" ? m.brand : "",
          doses: new Set(),
          roles: new Set(),
          diseases: [],
          class: classify(m.name),
        });
      }
      const entry = map.get(key);
      if (m.dose)  entry.doses.add(m.dose);
      if (m.role)  entry.roles.add(m.role);
      if (!entry.diseases.find((x) => x.id === d.id)) entry.diseases.push({ id: d.id, name: d.name, cat: d.cat, code: d.code });
      // Prefer a real brand over "—"
      if (!entry.brand && m.brand && m.brand !== "—") entry.brand = m.brand;
    });
  });
  return Array.from(map.values())
    .map((e) => ({ ...e, doses: Array.from(e.doses), roles: Array.from(e.roles) }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export default function FirstAidKit() {
  const kit = useMemo(buildKit, []);
  const [query, setQuery] = useState("");
  const [classFilter, setClassFilter] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return kit.filter((k) => {
      if (classFilter && k.class.id !== classFilter) return false;
      if (q.length < 2) return true;
      return (
        k.name.toLowerCase().includes(q) ||
        (k.brand || "").toLowerCase().includes(q) ||
        k.roles.join(" ").toLowerCase().includes(q) ||
        k.diseases.some((d) => d.name.toLowerCase().includes(q))
      );
    });
  }, [kit, query, classFilter]);

  // Class counts for filter chips
  const classCounts = useMemo(() => {
    const counts = {};
    kit.forEach((k) => { counts[k.class.id] = (counts[k.class.id] || 0) + 1; });
    return counts;
  }, [kit]);

  const classList = useMemo(() => {
    const seen = new Map();
    kit.forEach((k) => seen.set(k.class.id, k.class.label));
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [kit]);

  return (
    <div className="bg-white min-h-screen" data-testid="first-aid-kit-page">
      {/* HERO */}
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
                <span className="w-8 h-px bg-zinc-950" /> Formulary · Industrial First-Aid Kit
              </div>
              <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
                Stock the<br />
                <span className="bg-[#FFD700] text-zinc-950 px-2 inline-block">site clinic.</span>
              </h1>
              <p className="mt-6 text-lg text-zinc-700 max-w-xl">
                A dedup&apos;d <strong>{kit.length}-drug formulary</strong> aggregated from every disease in the
                catalog. Each entry lists the brand, dose ranges actually used, and every occupational
                indication — so an EHS officer can build the right kit in one go.
              </p>
            </div>
            <div className="md:col-span-4">
              <div className="grid grid-cols-2 gap-px bg-zinc-950 border-2 border-zinc-950">
                <Stat n={kit.length} l="Drugs" />
                <Stat n={DISEASES.length} l="Diseases" />
                <Stat n={classList.length} l="Classes" />
                <Stat n={Object.values(MEDICATIONS).flat().length} l="Indications" />
              </div>
            </div>
          </div>
          <div className="mt-6">
            <button
              data-testid="kit-pdf"
              onClick={() => buildFirstAidKitPdf(filtered.map((k) => ({
                name: k.name, brand: k.brand, doses: k.doses, diseases: k.diseases,
              })))}
              className="bg-zinc-950 text-white font-mono text-xs uppercase tracking-widest px-5 py-3 border-2 border-zinc-950 hover:bg-[#FFD700] hover:text-zinc-950 transition-colors inline-flex items-center gap-2"
            >
              <FileDown size={14} strokeWidth={2.5} /> Export Kit PDF
            </button>
          </div>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="border-b-2 border-zinc-950 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-5">
              <div className="flex items-center gap-2 border-2 border-zinc-950 bg-white px-3 py-2">
                <Search size={16} strokeWidth={2.5} />
                <input
                  data-testid="kit-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search generic, brand, indication or disease…"
                  className="w-full bg-transparent border-0 outline-none font-mono text-sm placeholder:text-zinc-500"
                />
              </div>
            </div>
            <div className="md:col-span-7 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              {filtered.length} of {kit.length} drug{filtered.length === 1 ? "" : "s"} shown
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">Class:</span>
            {classList.map(([id, label]) => (
              <button
                key={id}
                data-testid={`kit-class-${id}`}
                onClick={() => setClassFilter(classFilter === id ? null : id)}
                className={`border-2 border-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-all ${
                  classFilter === id ? "bg-zinc-950 text-white neo-shadow -translate-y-0.5" : "bg-white text-zinc-950 hover:neo-shadow"
                }`}
              >
                {label} · {classCounts[id]}
              </button>
            ))}
            {(classFilter || query) && (
              <button
                data-testid="kit-clear"
                onClick={() => { setClassFilter(null); setQuery(""); }}
                className="font-mono text-[10px] uppercase tracking-widest underline ml-2"
              >Clear</button>
            )}
          </div>
        </div>
      </section>

      {/* TABLE */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filtered.length === 0 ? (
          <div className="border-2 border-zinc-950 bg-[#FFD700] p-6 text-center" data-testid="kit-no-results">
            <div className="font-heading font-black text-2xl uppercase">No drugs match this filter</div>
          </div>
        ) : (
          <div className="border-2 border-zinc-950 overflow-x-auto" data-testid="kit-table">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-zinc-950 text-white font-mono uppercase text-[10px] tracking-[0.2em]">
                  <th className="px-3 py-3 border-r border-zinc-700 w-[220px]">Generic (Brand)</th>
                  <th className="px-3 py-3 border-r border-zinc-700 w-[80px]">Class</th>
                  <th className="px-3 py-3 border-r border-zinc-700 w-[260px]">Dose Range(s)</th>
                  <th className="px-3 py-3">Used For (Diseases)</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((k, i) => (
                  <tr
                    key={k.name}
                    data-testid={`kit-row-${k.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    className={i % 2 === 0 ? "bg-white" : "bg-zinc-50"}
                  >
                    <td className="px-3 py-3 border-t border-zinc-200 align-top">
                      <div className="flex items-start gap-2">
                        <Pill size={14} strokeWidth={2.5} className="mt-1 shrink-0" />
                        <div>
                          <div className="font-heading font-bold text-sm tracking-tight">{k.name}</div>
                          {k.brand && (
                            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                              {k.brand}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 border-t border-zinc-200 align-top">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-700">
                        {k.class.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 border-t border-zinc-200 align-top font-mono text-[11px] text-zinc-800 leading-snug">
                      {k.doses.map((d, j) => (
                        <div key={j} className="mb-1 last:mb-0">{d}</div>
                      ))}
                    </td>
                    <td className="px-3 py-3 border-t border-zinc-200 align-top">
                      <div className="flex flex-wrap gap-1">
                        {k.diseases.map((d) => {
                          const cat = CATEGORIES.find((c) => c.id === d.cat);
                          return (
                            <span
                              key={d.id}
                              title={d.name}
                              className={`${cat?.tone || "bg-zinc-200"} border-2 border-zinc-950 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest`}
                            >
                              {d.code} · {d.name}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-6 bg-[#FFD700] border-2 border-zinc-950 p-5" data-testid="kit-disclaimer">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-2 flex items-center gap-1.5">
            <AlertOctagon size={12} strokeWidth={2.75} /> Disclaimer
          </div>
          <p className="text-zinc-950 leading-relaxed text-sm">
            This formulary is a reference compendium aggregated from disease fact sheets in this app.
            It is <strong>not</strong> a prescriber-specific kit list. Stock, storage temperatures, controlled-drug
            schedules and local prescribing rules must be verified by a qualified pharmacist and occupational
            medicine physician before being placed into service.
          </p>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div className="bg-white px-4 py-3">
    <div className="font-heading font-black text-3xl leading-none">{n}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-1">{l}</div>
  </div>
);
